/**
 * DOS518 - Digital Operations System for Wildfire Management
 * 
 * Copyright (c) 2024-2025 Angelo Irtilaç
 * All rights reserved.
 * 
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, modification, or use is strictly prohibited.
 * 
 * ---
 * 
 * Author: Angelo Irtilaç
 * Qualifications: DOS (Direttore delle Operazioni di Spegnimento)
 *                39 years - Corpo Nazionale dei Vigili del Fuoco
 *                DOS Trainer & Instructor
 * 
 * Location: Carpi, Italy
 * Contact: [inserire email]
 * 
 * ---
 * 
 * This software implements official Italian Fire Department protocols:
 * - C.O.A.U. (Centro Operativo Aereo Unificato)
 * - SI.TA.C. (Sistema Tattico di Coordinamento)
 * - L.A.C.E.S. (Lookout, Awareness, Communication, Escape routes, Safety zones)
 * 
 * Developed with operational expertise acquired through decades of wildfire
 * emergency management and coordination of suppression operations.
 * 
 * For commercial licensing inquiries, please contact the author.
 */
// =========================================
// Export/Import Manager con IndexedDB
// Gestisce passaggio consegne DOS
// =========================================

import JSZip from 'jszip';
import { indexedDBManager } from './indexedDBManager';
import type { MediaItem } from '../types';

export interface DOS518Export {
  metadata: {
    version: string;
    exportDate: string;
    coauNumber: string;
    dosOperator: string;
  };
  data: {
    coau: any;
    coordinate: any[];
    risorse: any;
    sitac: any;
    laces: any;
    fraseologia: any[];
    media: MediaItem[];
  };
  mediaFiles: {
    [id: string]: {
      base64: string;
      type: string;
      filename: string;
    };
  };
}

class ExportImportManager {
  
  // =========================================
  // EXPORT COMPLETO CON MEDIA
  // =========================================
  
  async exportComplete(storeData: any): Promise<void> {
    try {
      console.log('📤 Inizio export completo...');
      
      // 1. Prepara i metadati
      const metadata = {
        version: '2.0.0',
        exportDate: new Date().toISOString(),
        coauNumber: storeData.coau?.numeroCoau || 'N/A',
        dosOperator: storeData.coau?.dos || 'N/A'
      };

      // 2. Prepara i dati (senza i blob dei media)
      const data = {
        coau: storeData.coau,
        coordinate: storeData.coordinate || [],
        risorse: {
          personale: storeData.personale || [],
          mezziTerrestri: storeData.mezziTerrestri || [],
          mezziAerei: storeData.mezziAerei || [],
          droni: storeData.droni || []
        },
        sitac: storeData.sitac || {},
        laces: storeData.laces || {},
        fraseologia: storeData.comunicazioni || [],
        media: storeData.media || [] // Solo metadati, non i blob
      };

      console.log(`📊 Media da esportare: ${data.media.length}`);

      // 3. Recupera i file media da IndexedDB
      const mediaFiles: DOS518Export['mediaFiles'] = {};
      
      for (const mediaItem of data.media) {
        try {
          const blob = await indexedDBManager.getMedia(mediaItem.id);
          
          if (blob) {
            const base64 = await indexedDBManager.blobToBase64(blob);
            const extension = this.getExtensionFromType(mediaItem.type);
            
            mediaFiles[mediaItem.id] = {
              base64,
              type: blob.type,
              filename: `${mediaItem.id}${extension}`
            };
            
            console.log(`✅ Media ${mediaItem.id} recuperato (${this.formatBytes(blob.size)})`);
          } else {
            console.warn(`⚠️ Media ${mediaItem.id} non trovato in IndexedDB`);
          }
        } catch (err) {
          console.error(`❌ Errore recupero media ${mediaItem.id}:`, err);
        }
      }

      console.log(`📦 Totale file media: ${Object.keys(mediaFiles).length}`);

      // 4. Crea il pacchetto completo
      const exportData: DOS518Export = {
        metadata,
        data,
        mediaFiles
      };

      // 5. Crea ZIP con JSZip
      const zip = new JSZip();

      // Aggiungi metadata.json
      zip.file('metadata.json', JSON.stringify(metadata, null, 2));

      // Aggiungi data.json (tutti i dati tranne i blob)
      zip.file('data.json', JSON.stringify(data, null, 2));

      // Aggiungi cartella media/ con tutti i file
      const mediaFolder = zip.folder('media');
      if (mediaFolder) {
        for (const [id, fileData] of Object.entries(mediaFiles)) {
          // Organizza per categoria
          const mediaItem = data.media.find(m => m.id === id);
          const category = mediaItem?.category || 'other';
          
          const categoryFolder = mediaFolder.folder(category);
          if (categoryFolder) {
            // Converti base64 a blob per lo ZIP
            const blob = indexedDBManager.base64ToBlob(fileData.base64, fileData.type);
            categoryFolder.file(fileData.filename, blob);
          }
        }
      }

      // 6. Genera il file ZIP
      console.log('🗜️ Compressione ZIP in corso...');
      const zipBlob = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });

      // 7. Download automatico
      const filename = `DOS518_${metadata.coauNumber}_${this.formatDateForFilename(metadata.exportDate)}.dos518`;
      this.downloadBlob(zipBlob, filename);

      console.log('✅ Export completato!');
      console.log(`📦 File: ${filename}`);
      console.log(`💾 Dimensione: ${this.formatBytes(zipBlob.size)}`);

      return;
      
    } catch (error) {
      console.error('❌ Errore durante export:', error);
      throw error;
    }
  }

  // =========================================
  // IMPORT COMPLETO CON MEDIA
  // =========================================
  
  async importComplete(file: File): Promise<DOS518Export> {
    try {
      console.log('📥 Inizio import completo...');
      console.log(`📦 File: ${file.name} (${this.formatBytes(file.size)})`);

      // 1. Carica e decomprimi il file ZIP
      const zip = await JSZip.loadAsync(file);
      console.log('✅ ZIP caricato');

      // 2. Leggi metadata.json
      const metadataFile = zip.file('metadata.json');
      if (!metadataFile) throw new Error('File metadata.json non trovato');
      
      const metadataText = await metadataFile.async('text');
      const metadata = JSON.parse(metadataText);
      console.log('✅ Metadata caricato');

      // 3. Leggi data.json
      const dataFile = zip.file('data.json');
      if (!dataFile) throw new Error('File data.json non trovato');
      
      const dataText = await dataFile.async('text');
      const data = JSON.parse(dataText);
      console.log('✅ Dati caricati');
      console.log(`📊 Media da importare: ${data.media.length}`);

      // 4. Importa i file media in IndexedDB
      const mediaFolder = zip.folder('media');
      const mediaFiles: DOS518Export['mediaFiles'] = {};

      if (mediaFolder) {
        // Pulisci il database prima di importare (opzionale)
        // await indexedDBManager.clearAllMedia();

        // Itera tutte le cartelle di categoria
        for (const [path, zipEntry] of Object.entries(mediaFolder.files)) {
          if (!zipEntry.dir && path.includes('/')) {
            try {
              // Estrai ID dal filename
              const filename = path.split('/').pop() || '';
              const id = filename.split('.')[0];

              // Carica il blob
              const blob = await zipEntry.async('blob');
              
              // Salva in IndexedDB
              const file = new File([blob], filename, { type: blob.type });
              await indexedDBManager.saveMedia(id, file);

              // Salva info per il return
              const base64 = await indexedDBManager.blobToBase64(blob);
              mediaFiles[id] = {
                base64,
                type: blob.type,
                filename
              };

              console.log(`✅ Media ${id} importato (${this.formatBytes(blob.size)})`);
              
            } catch (err) {
              console.error(`❌ Errore import media ${path}:`, err);
            }
          }
        }
      }

      console.log(`✅ Import completato! ${Object.keys(mediaFiles).length} media caricati`);

      // 5. Ritorna tutti i dati
      return {
        metadata,
        data,
        mediaFiles
      };

    } catch (error) {
      console.error('❌ Errore durante import:', error);
      throw error;
    }
  }

  // =========================================
  // UTILITY
  // =========================================

  private getExtensionFromType(type: 'photo' | 'video'): string {
    return type === 'photo' ? '.jpg' : '.mp4';
  }

  private formatDateForFilename(isoDate: string): string {
    const date = new Date(isoDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}${month}${day}_${hours}${minutes}`;
  }

  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Controlla se il browser supporta IndexedDB
  isIndexedDBSupported(): boolean {
    return 'indexedDB' in window;
  }
}

// Istanza singleton
export const exportImportManager = new ExportImportManager();

export default exportImportManager;
